import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import { getAll } from '@/api/produit/produitFace';
import { getAffectationProduitUnitaireAsync } from '@/api/affectation/index';
import calculDisponibiliteLc from '@/services/catalogue/disponibilite/disponibiliteLcADate';
import formatIsoDate from '@/services/general/formatIsoDate';
import formatDate from '@/services/general/formatDate';
import { getAllPlanning } from '@/api/dispo/index';
import { ajoutLignesCommandeProduitFaceFromEcranAjoutProduitFace } from '@/services/devisCommandes/ajoutProduit/ajoutProduitFace';
import getLogo from '@/services/general/getLogo';
import { genererCritereRechercheProduitFace, genererCritereRecherchePlanningProduitFace }
  from '@/services/catalogue/genererCriteresRecherche/genererCritereRechercheProduitFace';
import transform from '@/services/catalogue/filters/produitServerTableDataTransformer';
import { CLASS_NIVEAU_DISPONIBILITE } from '@/configuration/catalogue/disponibiliteConf';
import { SOUS_TYPE_AVOIR, SOUS_TYPE_AVOIR_SUR_ACOMPTE } from '@/configuration/devisConf';
import getPiecesJointeLogoByIds from '@/services/piecesJointes/getPiecesJointeLogoByIds';
import * as allTypes from '../mutation-type';
import * as types from './produitFace-mutation-type';

export function initialState(isCartographie = false) {
  return {
    produits: [],
    totalProduits: 0,
    brief: {
      autoFromCommande: null,
      fromDate: null,
      toDate: null,
      produitLocation: null,
      nonCommercialisable: false,
      societeId: null,
      agencesId: [],
      eclaire: null,
      secteurIds: [],
    },
    planningFaceUnitaire: {
      months: null,
      fromDate: null,
      toDate: null,
    },
    typeDCF: null,
    panier: [],
    regroupementPersonnalise: false,
    useEcranAjout: false,
    criteresRecherche: genererCritereRechercheProduitFace(isCartographie),
    lastPayload: {},
    critereRecherchePlanning: genererCritereRecherchePlanningProduitFace(),
  };
}

const state = initialState();

const namespaced = true;

const SIZES = {
  LARGE: 1550,
};

const OPTIONS_AFFICHAGE = {
  widescreen: 12, // Durée max en mois affichée à l'écran
  tablet: 6,
};

const mutations = {
  [types.SET_PRODUITS](state, produits) {
    state.produits = produits;
  },
  [types.SET_PLANNING_FACE_UNITAIRE](state, value) {
    state.planningFaceUnitaire = value;
  },
  [types.SET_TOTAL_PRODUITS](state, totalProduits) {
    state.totalProduits = totalProduits;
  },
  [types.SET_BRIEF](state, brief) {
    state.brief = brief;
  },
  [types.CLEAR_BRIEF](state) {
    state.brief = initialState().brief;
  },
  [types.CLEAR_PANIER](state) {
    state.panier = initialState().panier;
  },
  [types.ADD_LIGNE_IN_PANIER](state, ligne) {
    state.panier.push(ligne);
  },
  [types.ADD_LIGNE_IN_PANIER_AT_END](state, ligne) {
    state.panier.unshift(ligne);
  },
  [types.SET_PANIER](state, panier) {
    state.panier = panier;
  },
  [types.SET_LAST_PAYLOAD](state, payload) {
    state.lastPayload = payload;
  },
  [types.SET_TYPE_DCF](state, typeDCF) {
    state.typeDCF = typeDCF;
  },
  [types.SET_USE_ECRAN_AJOUT](state, useEcranAjout) {
    state.useEcranAjout = useEcranAjout;
  },
  [types.SET_CRITERES_RECHERCHE](state, criteresRecherche) {
    state.criteresRecherche = criteresRecherche;
  },
  [types.SET_CRITERES_RECHERCHE_PLANNING](state, criteresRecherche) {
    state.critereRecherchePlanning = criteresRecherche;
  },
  [types.DELETE_LIGNE_IN_PANIER](state, deleteObject) {
    const panierWithLigneToDelete = state.panier.filter((ligne) => {
      return ligne.isAlreadySelect === deleteObject.isAlreadySelect;
    });
    panierWithLigneToDelete.splice(deleteObject.index, 1);
    const panierWithoutLigneToDelete = state.panier.filter((ligne) => {
      return ligne.isAlreadySelect === !deleteObject.isAlreadySelect;
    });
    const panier = panierWithLigneToDelete.concat(panierWithoutLigneToDelete);
    state.panier = panier;
  },
  [types.SET_REGROUPEMENT_PERSONNALISE](state, regroupementPersonnalise) {
    state.regroupementPersonnalise = regroupementPersonnalise;
  },
  [types.SET_LOGO](state, item) {
    const produit = state.panier.find((ligne) => {
      return item.produitId === ligne.produitId && ligne.logo === undefined;
    });
    if (produit) produit.logo = item.logo ? item.logo : '';
  },
  [allTypes.RESET_STATE](state) {
    const s = initialState();
    Object.keys(state).forEach(key => {
      state[key] = s[key];
    });
  },
};

const actions = {
  async fetchProduits({ commit, dispatch }, data) {
    const { data: { result: { totalCount, items } } } = await getAll(data);
    commit(types.SET_TOTAL_PRODUITS, totalCount);
    commit(types.SET_PRODUITS, items.map(item => ({ ...item, affectations: [] })));
    dispatch('fetchAffectations', { fromDate: data.query.fromDate, toDate: data.query.toDate });
    return {};
  },
  async fetchAffectations({ commit, state, getters }, { fromDate, toDate }) {
    const critereRecherche = cloneDeep(getters.critereRecherchePlanning);
    const paramsAffectations = state.produits.map(item => ({
      id: item.id,
      dureeAffectation: {
        dateDebut: formatIsoDate(fromDate),
        dateFin: formatIsoDate(toDate),
      },
    }));
    const { data: { result } } = await getAffectationProduitUnitaireAsync(
      paramsAffectations, false, critereRecherche.avecAffectationReseau,
    );
    const products = state.produits.map(item => {
      item.affectations = [];
      if (result[item.id]) {
        item.affectations = result[item.id];
      }
      item.affectations = item.affectations.sort((a, b) => {
        const dateA = new Date(a.dureeAffectation.dateDebut);
        const dateB = new Date(b.dureeAffectation.dateDebut);
        return dateA - dateB;
      });
      item.affectations.forEach(affectation => {
        affectation.dureeAffectation
          .dateDebut = formatDate(affectation.dureeAffectation.dateDebut);
        affectation.dureeAffectation
          .dateFin = formatDate(affectation.dureeAffectation.dateFin);
      });
      return item;
    });
    commit(types.SET_PRODUITS, products);
  },
  async loadProduitsFace({ commit }, data) {
    const response = await getAll(data);
    const produits = response.data.result.items;
    const allIds = produits.map(p => p.id);
    if (allIds.length > 0) {
      const paramsAffectation = [];
      produits.forEach((item) => {
        item.selected = false;
        item.affectations = [];
        paramsAffectation.push({
          id: item.id,
          dureeAffectation: {
            dateDebut: formatIsoDate(data.query.dureeAffectation.dateDebut),
            dateFin: formatIsoDate(data.query.dureeAffectation.dateFin),
          },
        });
      });
      const resultAffectation = (await getAffectationProduitUnitaireAsync(paramsAffectation,
        false, true))
        .data
        .result;
      produits.forEach((item) => {
        if (!resultAffectation[item.id]) return;
        item.affectations = resultAffectation[item.id];
        item.affectations = item.affectations.sort((a, b) => {
          const dateA = new Date(a.dureeAffectation.dateDebut);
          const dateB = new Date(b.dureeAffectation.dateDebut);
          return dateA - dateB;
        });
      });
    }
    if (!data.withoutImage) await getPiecesJointeLogoByIds(response.data.result.items);
    commit(types.SET_LAST_PAYLOAD, transform(data));
    commit(types.SET_TOTAL_PRODUITS, response.data.result.totalCount);
    commit(types.SET_PRODUITS, produits);
    return {};
  },
  async loadProduitsWithoutDispo({ commit }, data) {
    const response = (await getAll(data)).data.result;
    commit(types.SET_TOTAL_PRODUITS, response.totalCount);
    commit(types.SET_PRODUITS, response.items);
    return {};
  },
  async loadProduits({ commit }, data) {
    const response = await getAll(data);
    const produits = response.data.result.items;
    const allIds = produits.map(p => p.id);
    if (allIds.length > 0 && data.query.fromDate && data.query.toDate) {
      let planning = null;
      if (!data.sansDispo) {
        planning = await getAllPlanning(
          allIds, data.query.fromDate, data.query.toDate,
        );
      }
      const paramsAffectation = [];
      produits.forEach((item) => {
        item.affectations = [];
        paramsAffectation.push({
          id: item.id,
          dureeAffectation: {
            dateDebut: formatIsoDate(data.query.fromDate),
            dateFin: formatIsoDate(data.query.toDate),
          },
        });
        if (planning !== null) {
          const detailDispo = calculDisponibiliteLc(planning.data.result[item.id]);
          item.decoration = {
            resteAVendreMax: item.controleDisponibilite ? detailDispo.resteAVendreMax * 100 : 100,
            dispoClass: item.controleDisponibilite
              ? detailDispo.disponibiliteCss : CLASS_NIVEAU_DISPONIBILITE[3],
          };
        }
      });
      const resultAffectation = (await getAffectationProduitUnitaireAsync(paramsAffectation,
        false, true))
        .data
        .result;
      produits.forEach((item) => {
        if (!resultAffectation[item.id]) {
          return;
        }
        item.affectations = resultAffectation[item.id];
        item.affectations = item.affectations.sort((a, b) => {
          const dateA = new Date(a.dureeAffectation.dateDebut);
          const dateB = new Date(b.dureeAffectation.dateDebut);
          return dateA - dateB;
        });
        item.affectations.forEach(affectation => {
          affectation.dureeAffectation
            .dateDebut = formatDate(affectation.dureeAffectation.dateDebut);
          affectation.dureeAffectation
            .dateFin = formatDate(affectation.dureeAffectation.dateFin);
        });
      });
    }
    commit(types.SET_TOTAL_PRODUITS, response.data.result.totalCount);
    commit(types.SET_PRODUITS, produits);
    return {};
  },
  setFaceUnitaireMonthsInView({ getters, commit }, months) {
    const current = getters.planningFaceUnitaire.months;
    if (current !== months) {
      const w = window.innerWidth;
      if (months === 12 && w >= SIZES.LARGE) {
        commit(types.SET_PLANNING_FACE_UNITAIRE, {
          ...getters.planningFaceUnitaire, months: months,
        });
      } else {
        commit(types.SET_PLANNING_FACE_UNITAIRE, {
          ...getters.planningFaceUnitaire, months: months,
        });
      }
    }
  },
  setPlanningFaceUnitaire({ getters, commit }, payload) {
    const current = cloneDeep(getters.planningFaceUnitaire);
    const w = window.innerWidth;
    let format = 'tablet';
    if (w >= SIZES.LARGE) format = 'widescreen';
    current.months = OPTIONS_AFFICHAGE[format];

    if (payload) {
      current.fromDate = moment(payload.fromDate).startOf('month').toDate();
      current.toDate = moment(current.fromDate).add(current.months, 'months').add(-1, 'days').endOf('day')
        .toDate();
    } else {
      current.fromDate = current.fromDate ? moment(current.fromDate).startOf('month').toDate() : moment().startOf('month').toDate();
      current.toDate = moment(current.fromDate).add(current.months, 'months').add(-1, 'days').endOf('day')
        .toDate();
    }
    if (getters.planningFaceUnitaire.months !== current.months
      || getters.planningFaceUnitaire.fromDate !== current.fromDate
      || getters.planningFaceUnitaire.toDate !== current.toDate) {
      commit(types.SET_PLANNING_FACE_UNITAIRE, current);
    }
  },
  async addLigneInPanier({ commit, getters, rootState }, data) {
    const ligne = await ajoutLignesCommandeProduitFaceFromEcranAjoutProduitFace(
      data.produitFaceId, data.produitLocation, data.dateDebut, data.dateFin, getters.typeDCF,
    );
    // Si la ligne est ajouté a un avoir, la quantite dois etre négative.
    if (rootState.commande.commande?.sousType === SOUS_TYPE_AVOIR
      || rootState.commande.commande?.sousType === SOUS_TYPE_AVOIR_SUR_ACOMPTE
    ) {
      ligne.quantite *= -1;
      if (ligne.totalHt) ligne.totalHt = ligne.prixUnitaire * ligne.quantite;
      if (ligne.lignesCommande && Array.isArray(ligne.lignesCommande)) {
        ligne.lignesCommande.forEach(sousLigne => {
          sousLigne.quantite *= -1;
          if (sousLigne.totalHt) sousLigne.totalHt = sousLigne.prixUnitaire * sousLigne.quantite;
        });
      }
    }
    commit(types.ADD_LIGNE_IN_PANIER, ligne);
  },
  addProduitInPanier({ commit }, data) {
    const produitToAdd = {
      produit: data.produit,
      dateDebut: data.dateDebut,
      dateFin: data.dateFin,
      isAlreadySelect: false,
    };
    commit(types.ADD_LIGNE_IN_PANIER_AT_END, produitToAdd);
  },
  loadLogoPanier({ commit, getters }, isLigne) {
    getters.panier.filter((produit) => {
      return produit.logo === undefined;
    }).forEach(async (produit) => {
      const logo = {
        produitId: isLigne ? produit.produitId : produit.produit.id,
        logo: await getLogo(isLigne ? produit.produitId : produit.produit.id),
      };
      commit(types.SET_LOGO, logo);
    });
  },
  updateStartDateInPlanningView({ getters, commit }, startDate) {
    const month = moment(startDate).month();
    const settings = { ...getters.planningFaceUnitaire };
    const startMonthInView = moment(settings.fromDate).month();
    if (month !== startMonthInView) {
      settings.fromDate = moment(startDate).startOf('month').toDate();
      settings.toDate = moment(settings.fromDate)
        .add(settings.months, 'months').add(-1, 'days').endOf('day')
        .toDate();
      commit(types.SET_PLANNING_FACE_UNITAIRE, settings);
    }
  },
  updateMonthsInPlanningView({ commit, getters }, payload) {
    const settings = { ...getters.planningFaceUnitaire };
    settings.fromDate = moment(settings.fromDate).startOf('month').toDate();
    settings.months = payload;
    settings.toDate = moment(settings.fromDate)
      .add(settings.months, 'months').add(-1, 'days').endOf('day')
      .toDate();
    commit(types.SET_PLANNING_FACE_UNITAIRE, settings);
  },
};

const getters = {
  produits: state => {
    return state.produits;
  },
  totalProduits: state => {
    return state.totalProduits;
  },
  planningFaceUnitaire: state => {
    return state.planningFaceUnitaire;
  },
  brief: state => {
    return state.brief;
  },
  panier: state => {
    return state.panier;
  },
  regroupementPersonnalise: state => {
    return state.regroupementPersonnalise;
  },
  typeDCF: state => {
    return state.typeDCF;
  },
  useEcranAjout: state => {
    return state.useEcranAjout;
  },
  criteresRecherche: state => {
    return state.criteresRecherche;
  },
  lastPayload: state => {
    return state.lastPayload;
  },
  critereRecherchePlanning: state => {
    return state.critereRecherchePlanning;
  },
};

export default {
  namespaced,
  state,
  mutations,
  actions,
  getters,
};
